import {
  elementShadowRoot,
  normalizeAddressText,
  parsePlaceAddress,
  parseRenderedPlaceAddress,
  readDeepText,
  registerClosedShadow,
  zipKey,
  type PlaceAddressSource,
  type StreetAddress,
} from "./place-address";

/** Pinned Maps JS channel — not `weekly`. UI Kit is still pre-GA. */
export const GOOGLE_MAPS_JS_VERSION = "quarterly";

export function googleMapsApiKey(): string {
  return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "").trim();
}

export function mapsLookupEnabled(): boolean {
  return googleMapsApiKey().length > 0;
}

type MapsNamespace = {
  importLibrary?: (name: string, ...rest: unknown[]) => Promise<unknown>;
  __ib__?: () => void;
};

type GoogleNamespace = {
  maps?: MapsNamespace;
};

let loading: Promise<void> | null = null;
let attachShadowPatched = false;

/**
 * Basic Autocomplete's `gmp-select` only has `place.id`. Compact Place Details
 * `.place` is only id/location/viewport. `fetchFields()` is off-limits.
 * The street the user clicked lives in Google's closed shadow, so remember the
 * root at `attachShadow` and read that visible text.
 */
function patchClosedShadowCapture(): void {
  if (attachShadowPatched) return;
  attachShadowPatched = true;
  const original = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function patchedAttachShadow(this: Element, init: ShadowRootInit) {
    const root = original.call(this, init);
    if (init.mode === "closed") registerClosedShadow(this, root);
    return root;
  };
}

function googleRoot(): GoogleNamespace {
  const win = window as Window & { google?: GoogleNamespace };
  win.google ??= {};
  win.google.maps ??= {};
  return win.google;
}

/**
 * Official Maps JS bootstrap loader, with a pinned version instead of `weekly`.
 * https://developers.google.com/maps/documentation/javascript/places-ui-kit/get-started
 */
function installMapsBootstrap(key: string): void {
  const maps = googleRoot().maps!;
  if (typeof maps.importLibrary === "function") return;

  const pending = new Set<string>();
  const params: Record<string, string> = { key, v: GOOGLE_MAPS_JS_VERSION };
  let scriptPromise: Promise<void> | null = null;

  const bootstrapImport: NonNullable<MapsNamespace["importLibrary"]> = (name, ...rest) => {
    pending.add(name);
    scriptPromise ??= new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      const search = new URLSearchParams();
      search.set("libraries", [...pending].join(","));
      for (const [param, value] of Object.entries(params)) {
        search.set(param.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), value);
      }
      search.set("callback", "google.maps.__ib__");
      maps.__ib__ = () => resolve();
      script.src = `https://maps.googleapis.com/maps/api/js?${search.toString()}`;
      script.async = true;
      script.dataset.googleMapsBootstrap = "true";
      script.onerror = () => reject(new Error("The Google Maps JavaScript API could not load."));
      document.head.append(script);
    });
    return scriptPromise.then(() => {
      const loaded = googleRoot().maps?.importLibrary;
      if (typeof loaded !== "function" || loaded === bootstrapImport) {
        throw new Error("The Google Maps JavaScript API did not install importLibrary.");
      }
      return loaded(name, ...rest);
    });
  };
  maps.importLibrary = bootstrapImport;
}

export function loadGoogleMapsPlaces(): Promise<void> {
  const key = googleMapsApiKey();
  if (!key) return Promise.reject(new Error("Google Maps key is not configured"));
  if (loading) return loading;
  patchClosedShadowCapture();
  loading = (async () => {
    installMapsBootstrap(key);
    const importLibrary = googleRoot().maps?.importLibrary;
    if (typeof importLibrary !== "function") throw new Error("The Google Maps JavaScript API could not start.");
    await importLibrary("places");
  })().catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

type AutocompleteElement = HTMLElement & {
  placeholder?: string;
  name?: string;
  value?: string;
  noInputIcon?: boolean;
};

type AutocompleteCtor = new (options?: {
  includedRegionCodes?: string[];
  includedPrimaryTypes?: string[];
  requestedRegion?: string;
  noInputIcon?: boolean;
  placeholder?: string;
}) => AutocompleteElement;

type PlaceDetailsElement = HTMLElement & {
  place?: PlaceAddressSource;
};

export function placeIdFromSelectEvent(event: Event): string | null {
  const selected = event as Event & {
    place?: { id?: string | null };
    placePrediction?: { placeId?: string | null };
  };
  const id = selected.place?.id ?? selected.placePrediction?.placeId;
  return id?.trim() ? id : null;
}

type Formattable = { text?: unknown; toString?: () => string };

function formattableText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  const record = value as Formattable;
  if (typeof record.text === "string") return record.text.trim();
  if (typeof record.toString === "function") {
    const asString = record.toString();
    if (asString && asString !== "[object Object]") return asString.trim();
  }
  return "";
}

function partNames(node: HTMLElement): string {
  const fromAttr = node.getAttribute("part") ?? "";
  const fromDom = "part" in node && node.part ? Array.from(node.part).join(" ") : "";
  return `${fromAttr} ${fromDom}`.toLowerCase();
}

function predictionItemText(event: Event, widget?: HTMLElement | null): string {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    const part = partNames(node);
    if (!part.includes("prediction-item") || part.includes("icon")) continue;
    const text = normalizeAddressText(node.innerText || readDeepText(node));
    if (text) return text;
  }
  const root = widget ? elementShadowRoot(widget) : null;
  const selectedItem =
    root?.querySelector('[part~="prediction-item-selected"]') ??
    root?.querySelector('[part~="prediction-item"]');
  if (selectedItem instanceof HTMLElement) {
    const text = normalizeAddressText(selectedItem.innerText || readDeepText(selectedItem));
    if (text) return text;
  }
  return "";
}

const rememberedPrediction = new WeakMap<HTMLElement, string>();

export function rememberAutocompletePrediction(widget: HTMLElement, event: Event): void {
  patchClosedShadowCapture();
  const text = predictionItemText(event, widget);
  if (text) rememberedPrediction.set(widget, text);
}

export function bindAutocompletePredictionCapture(widget: HTMLElement): () => void {
  patchClosedShadowCapture();
  const remember = (event: Event) => rememberAutocompletePrediction(widget, event);
  const targets = new Set<EventTarget>();
  const listen = (target: EventTarget) => {
    if (targets.has(target)) return;
    targets.add(target);
    target.addEventListener("pointerdown", remember, true);
    target.addEventListener("pointerover", remember, true);
    target.addEventListener("keydown", remember, true);
  };
  listen(widget);
  const root = elementShadowRoot(widget);
  if (root) listen(root);
  const lateShadow = window.setInterval(() => {
    const next = elementShadowRoot(widget);
    if (next) listen(next);
  }, 50);
  const stopLooking = window.setTimeout(() => window.clearInterval(lateShadow), 4000);
  return () => {
    window.clearInterval(lateShadow);
    window.clearTimeout(stopLooking);
    for (const target of targets) {
      target.removeEventListener("pointerdown", remember, true);
      target.removeEventListener("pointerover", remember, true);
      target.removeEventListener("keydown", remember, true);
    }
  };
}

export function selectedPlaceLabel(event: Event, widget?: HTMLElement | null): string {
  const selected = event as Event & {
    placePrediction?: {
      text?: unknown;
      mainText?: unknown;
      secondaryText?: unknown;
    };
    place?: { displayName?: unknown; formattedAddress?: string | null; formatted_address?: string | null };
  };
  const fromPrediction = formattableText(selected.placePrediction?.text);
  if (fromPrediction) return fromPrediction;
  const main = formattableText(selected.placePrediction?.mainText);
  const secondary = formattableText(selected.placePrediction?.secondaryText);
  if (main && secondary) return `${main}, ${secondary}`;
  if (main) return main;
  const fromPlace =
    formattableText(selected.place?.displayName) ||
    (selected.place?.formattedAddress ?? selected.place?.formatted_address ?? "").trim();
  if (fromPlace) return fromPlace;
  const fromItem = predictionItemText(event, widget);
  if (fromItem) return fromItem;
  const remembered = (widget && rememberedPrediction.get(widget)) || "";
  if (remembered) return remembered;
  if (widget) {
    const value = autocompleteInputValue(widget, event);
    if (value.includes(",")) return value;
  }
  return "";
}

export function parseSelectedPlace(event: Event, widget?: HTMLElement | null): StreetAddress | null {
  const label = selectedPlaceLabel(event, widget);
  if (!label) return null;
  return parsePlaceAddress({ formattedAddress: label });
}

export function autocompleteInputValue(element: HTMLElement, event?: Event): string {
  if (event?.target && "value" in event.target && typeof (event.target as HTMLInputElement).value === "string") {
    return (event.target as HTMLInputElement).value;
  }
  if (element instanceof HTMLInputElement) return element.value;
  const value = (element as AutocompleteElement).value;
  return typeof value === "string" ? value : "";
}

function decorateAutocomplete(
  element: AutocompleteElement,
  options?: { placeholder?: string; ariaLabel?: string },
): HTMLElement {
  const placeholder = options?.placeholder ?? "Start typing the street";
  element.setAttribute("placeholder", placeholder);
  element.placeholder = placeholder;
  element.setAttribute("no-input-icon", "");
  element.noInputIcon = true;
  if (options?.ariaLabel) element.setAttribute("aria-label", options.ariaLabel);
  element.setAttribute("data-street-lookup", "google");
  return element;
}

export async function createBasicPlaceAutocomplete(options?: {
  placeholder?: string;
  ariaLabel?: string;
}): Promise<HTMLElement> {
  await loadGoogleMapsPlaces();
  const library = (await googleRoot().maps!.importLibrary!("places")) as {
    BasicPlaceAutocompleteElement: AutocompleteCtor;
  };
  const Ctor = library.BasicPlaceAutocompleteElement;
  let element: AutocompleteElement;
  try {
    element = new Ctor({
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ["street_address", "premise", "subpremise"],
      requestedRegion: "us",
      noInputIcon: true,
      placeholder: options?.placeholder ?? "Start typing the street",
    });
  } catch {
    element = new Ctor({ includedRegionCodes: ["us"] });
  }
  return decorateAutocomplete(element, options);
}

export function createAddressOnlyPlaceDetails(kind: "compact" | "full" = "compact"): HTMLElement {
  const details = document.createElement(kind === "full" ? "gmp-place-details" : "gmp-place-details-compact");
  if (kind === "compact") details.setAttribute("orientation", "vertical");
  details.style.width = "360px";
  details.style.display = "block";
  details.style.colorScheme = "light";
  const request = document.createElement("gmp-place-details-place-request");
  const config = document.createElement("gmp-place-content-config");
  // Address-only content bills Places UI Kit Query, not Place Details Pro.
  config.append(document.createElement("gmp-place-address"));
  details.append(request, config);
  return details;
}

type PlacesLibrary = {
  PlaceDetailsCompactElement?: new (options?: { orientation?: string }) => HTMLElement;
  PlaceDetailsElement?: new () => HTMLElement;
  PlaceDetailsPlaceRequestElement?: new () => HTMLElement;
  PlaceContentConfigElement?: new () => HTMLElement;
};

async function createLibraryPlaceDetails(kind: "compact" | "full"): Promise<HTMLElement | null> {
  const importLibrary = googleRoot().maps?.importLibrary;
  if (typeof importLibrary !== "function") return null;
  try {
    const library = (await importLibrary("places")) as PlacesLibrary;
    let details: HTMLElement;
    if (kind === "full") {
      if (!library.PlaceDetailsElement) return null;
      details = new library.PlaceDetailsElement();
    } else if (!library.PlaceDetailsCompactElement) {
      return null;
    } else {
      details = new library.PlaceDetailsCompactElement({ orientation: "vertical" });
      details.setAttribute("orientation", "vertical");
    }
    details.style.width = "360px";
    details.style.display = "block";
    details.style.colorScheme = "light";
    const request = library.PlaceDetailsPlaceRequestElement
      ? new library.PlaceDetailsPlaceRequestElement()
      : document.createElement("gmp-place-details-place-request");
    const config = library.PlaceContentConfigElement
      ? new library.PlaceContentConfigElement()
      : document.createElement("gmp-place-content-config");
    if (!config.querySelector("gmp-place-address")) {
      config.append(document.createElement("gmp-place-address"));
    }
    details.append(request, config);
    return details;
  } catch {
    return null;
  }
}

type PlaceRef = string | { id?: string | null };

function placeIdFromRef(place: PlaceRef): string {
  const raw = typeof place === "string" ? place : (place.id ?? "");
  return raw.replace(/^places\//, "").trim();
}

export function setPlaceDetailsPlace(details: HTMLElement, place: PlaceRef): void {
  const request = details.querySelector("gmp-place-details-place-request") as
    | (HTMLElement & { place?: unknown })
    | null;
  if (!request) return;
  const placeId = placeIdFromRef(place);
  if (!placeId) return;
  // JS `place` accepts a Place, id, or resource name — not gmp-select's `{ id }`.
  // The HTML attribute must be a resource name (`places/ID`).
  request.place = placeId;
  request.setAttribute("place", `places/${placeId}`);
}

const DETAILS_LOAD_MS = 8000;
const DETAILS_TEXT_WAIT_MS = 1500;
const DETAILS_ZIP_WAIT_MS = 4500;

function waitForDetailsSignal(details: HTMLElement, timeoutMs: number): Promise<"load" | "error" | "timeout"> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (reason: "load" | "error" | "timeout") => {
      if (done) return;
      done = true;
      details.removeEventListener("gmp-load", onLoad);
      details.removeEventListener("gmp-error", onError);
      details.removeEventListener("gmp-requesterror", onError);
      window.clearTimeout(timer);
      resolve(reason);
    };
    const onLoad = () => finish("load");
    const onError = () => finish("error");
    details.addEventListener("gmp-load", onLoad);
    details.addEventListener("gmp-error", onError);
    details.addEventListener("gmp-requesterror", onError);
    const timer = window.setTimeout(() => finish("timeout"), timeoutMs);
  });
}

function detailsPlaceSource(details: HTMLElement): PlaceAddressSource | null {
  const fromDetails = (details as PlaceDetailsElement).place ?? null;
  const request = details.querySelector("gmp-place-details-place-request") as
    | (HTMLElement & { place?: PlaceAddressSource | string | null })
    | null;
  const fromRequest = request && typeof request.place === "object" && request.place ? request.place : null;
  if (fromDetails?.formattedAddress || fromDetails?.formatted_address || fromDetails?.addressComponents) {
    return fromDetails;
  }
  if (fromRequest?.formattedAddress || fromRequest?.formatted_address || fromRequest?.addressComponents) {
    return fromRequest;
  }
  return fromDetails ?? fromRequest;
}

function waitForParsedAddress(
  details: HTMLElement,
  waitMs: number,
  options?: { requirePostalCode?: boolean },
): Promise<StreetAddress | null> {
  const requirePostalCode = options?.requirePostalCode ?? false;
  const attempt = (strict = requirePostalCode) => {
    const parsed = parseRenderedPlaceAddress(details, detailsPlaceSource(details));
    if (!parsed) return null;
    if (strict && !zipKey(parsed.postalCode)) return null;
    return parsed;
  };
  const first = attempt();
  if (first) return Promise.resolve(first);
  return new Promise((resolve) => {
    let done = false;
    const finish = (parsed: StreetAddress | null) => {
      if (done) return;
      done = true;
      obs.disconnect();
      window.clearTimeout(timer);
      window.clearInterval(poll);
      resolve(parsed);
    };
    const obs = new MutationObserver(() => {
      const parsed = attempt();
      if (parsed) finish(parsed);
    });
    const watch = (node: Node) => obs.observe(node, { subtree: true, childList: true, characterData: true });
    watch(details);
    const root = elementShadowRoot(details);
    if (root) watch(root);
    const poll = window.setInterval(() => {
      const next = elementShadowRoot(details);
      if (next) watch(next);
      const parsed = attempt();
      if (parsed) finish(parsed);
    }, 50);
    const timer = window.setTimeout(() => finish(attempt(false)), waitMs);
  });
}

async function mountPlaceDetails(
  host: HTMLElement,
  placeId: string,
  options?: { timeoutMs?: number; requirePostalCode?: boolean },
  kind: "compact" | "full" = "compact",
): Promise<StreetAddress | null> {
  let details: HTMLElement;
  if (mapsLookupEnabled()) {
    try {
      await loadGoogleMapsPlaces();
      const tag = kind === "full" ? "gmp-place-details" : "gmp-place-details-compact";
      await Promise.race([
        customElements.whenDefined(tag),
        new Promise<void>((resolve) => window.setTimeout(resolve, 2000)),
      ]);
      details = (await createLibraryPlaceDetails(kind)) ?? createAddressOnlyPlaceDetails(kind);
    } catch {
      details = createAddressOnlyPlaceDetails(kind);
    }
  } else {
    details = createAddressOnlyPlaceDetails(kind);
  }
  details.style.display = "block";
  details.style.width = "360px";
  host.replaceChildren(details);
  const signal = waitForDetailsSignal(details, options?.timeoutMs ?? DETAILS_LOAD_MS);
  // Always a Place ID string. gmp-select's `{ id }` is not a Place instance.
  setPlaceDetailsPlace(details, placeId);
  const outcome = await signal;
  if (outcome === "error") {
    const parsed = parseRenderedPlaceAddress(details, detailsPlaceSource(details));
    details.remove();
    if (parsed && (!options?.requirePostalCode || zipKey(parsed.postalCode))) return parsed;
    return null;
  }
  const parsed = await waitForParsedAddress(
    details,
    options?.requirePostalCode ? DETAILS_ZIP_WAIT_MS : DETAILS_TEXT_WAIT_MS,
    { requirePostalCode: options?.requirePostalCode },
  );
  details.remove();
  return parsed;
}

export async function resolvePlaceAddressFromId(
  placeId: string,
  host: HTMLElement,
  options?: { timeoutMs?: number; requirePostalCode?: boolean },
): Promise<StreetAddress | null> {
  patchClosedShadowCapture();
  // Place Details needs a real, on-page box. Google's samples re-append and
  // `display:block`. Hidden / 1px hosts often gmp-error or never paint ZIP.
  // Compact previously lacked a formatted address on live preview; when ZIP is
  // required, try full Place Details (address-only) first. Do not call
  // place.fetchFields() — that bills Places API Details.
  host.classList.add("is-resolving");
  try {
    const order: Array<"compact" | "full"> = options?.requirePostalCode
      ? ["full", "compact"]
      : ["compact"];
    let best: StreetAddress | null = null;
    for (const kind of order) {
      const parsed = await mountPlaceDetails(host, placeId, options, kind);
      if (zipKey(parsed?.postalCode)) return parsed;
      best ??= parsed;
      if (!options?.requirePostalCode) return parsed;
    }
    return best;
  } finally {
    host.classList.remove("is-resolving");
    host.replaceChildren();
  }
}
